import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom, SCOPES, useHasScope } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import { useNotification } from '@data/notification';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import type { AxiosResponse, AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * 候補値取り込みAPI
 */
export const useApproveCalibrationParameterCandidatesAPI = (): {
  state: APIState;
  postApproveCalibrationParameterCandidates: (
    vehicleId: string,
    target: Array<string>,
  ) => Promise<boolean>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getHasScope = useHasScope();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const postApproveCalibrationParameterCandidatesAPI = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      target: Array<string>,
    ): Promise<Result<boolean, AxiosResponse>> => {
      try {
        await fmsAPI.post(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/calibration_parameter_candidates/approve`,
          target,
        );
        return new Success(true);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );
  const postApproveCalibrationParameterCandidates = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, target: Array<string>) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) && isNullOrUndefined(environment)) {
          notifyError({
            message: t(
              'api.fms.post_approve_calibration_parameter_candidates',
              {
                status: 'failed',
              },
            ),
          });
          setState('hasError');
          return false;
        }
        setState('loading');
        if (!getHasScope(SCOPES.ApproveCalibrationParameterCandidates)) {
          setState('hasError');
          return false;
        }
        const res = await postApproveCalibrationParameterCandidatesAPI(
          project!.id,
          environment!.environment_id,
          vehicleId,
          target,
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t(
              'api.fms.post_approve_calibration_parameter_candidates',
              {
                status: 'failed',
              },
            )}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return false;
        }
        notifySuccess({
          message: t('api.fms.post_approve_calibration_parameter_candidates', {
            status: 'successfully',
          }),
        });
        setState('hasValue');
        return res.value;
      },
      [
        notifyError,
        notifySuccess,
        postApproveCalibrationParameterCandidatesAPI,
        getHasScope,
        t,
        getErrorMessage,
      ],
    ),
  );

  return {
    state,
    postApproveCalibrationParameterCandidates,
  };
};
